import React from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, 
  Phone, 
  Shield, 
  Clock, 
  Zap, 
  ArrowRight, 
  MessageSquare, 
  Globe,
  FileText,
  Users,
  Bell,
  Lock
} from 'lucide-react';

interface Emergency911Props {
  setCurrentPage: (page: string) => void;
}

const Emergency911: React.FC<Emergency911Props> = ({ setCurrentPage }) => {
  const emergencyServices = [
    {
      icon: Shield,
      title: "Active Threat Response",
      description: "Immediate response to active security threats and breaches",
      action: "tel:+18889917050"
    },
    {
      icon: Lock,
      title: "Ransomware Emergency",
      description: "Urgent assistance with ransomware and malware incidents",
      action: "tel:+18889917050"
    },
    {
      icon: Bell,
      title: "Security Incident",
      description: "Report and respond to security incidents and suspicious activities",
      action: "tel:+18889917050"
    }
  ];

  const responseTeams = [
    {
      icon: Users,
      title: "Incident Response Team",
      items: [
        "24/7 emergency response",
        "Rapid threat containment",
        "Evidence preservation",
        "Incident investigation"
      ]
    },
    {
      icon: Shield,
      title: "Security Operations",
      items: [
        "Real-time monitoring",
        "Threat detection",
        "System lockdown",
        "Network protection"
      ]
    },
    {
      icon: FileText,
      title: "Recovery Team",
      items: [
        "System restoration",
        "Data recovery",
        "Business continuity",
        "Post-incident analysis"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Emergency Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-6"
          >
            <AlertTriangle className="h-20 w-20 text-red-500 animate-pulse" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            <span className="text-red-500">Emergency Response Center</span>
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <a 
              href="tel:+18889917050"
              className="inline-flex items-center justify-center px-8 py-4 bg-red-500 text-white rounded-full text-xl font-bold hover:bg-red-600 transition-colors animate-pulse"
            >
              <Phone className="h-6 w-6 mr-2" />
              Emergency Hotline: (888) 991-7050
            </a>
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-muted max-w-3xl mx-auto"
          >
            24/7 Emergency Response for Critical Security Incidents
          </motion.p>
        </div>

        {/* Emergency Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          {emergencyServices.map((service, index) => (
            <a
              key={index}
              href={service.action}
              className="glass-panel rounded-xl p-8 hover:scale-105 transition-transform cursor-pointer border-2 border-red-500/20 hover:border-red-500/40"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-full mb-6 mx-auto">
                <service.icon className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-2xl font-bold text-center mb-4">{service.title}</h3>
              <p className="text-muted text-center">{service.description}</p>
              <div className="mt-6 text-center">
                <span className="inline-flex items-center text-red-500 font-semibold">
                  Call Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </div>
            </a>
          ))}
        </motion.div>

        {/* Response Teams */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Emergency Response Teams</h2>
            <p className="text-lg text-muted">
              Specialized teams ready to respond to your security emergency
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {responseTeams.map((team, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <team.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{team.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {team.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <Clock className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Emergency Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="glass-panel rounded-2xl p-8 md:p-12 mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-center">Emergency Response Protocol</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
                Immediate Actions
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mr-3 mt-1">1</span>
                  <span className="text-muted">Call our emergency hotline immediately</span>
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mr-3 mt-1">2</span>
                  <span className="text-muted">Isolate affected systems if possible</span>
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mr-3 mt-1">3</span>
                  <span className="text-muted">Document the incident details</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Shield className="h-6 w-6 text-primary mr-2" />
                Have Ready
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-3 mt-1" />
                  <span className="text-muted">Incident timeline and details</span>
                </li>
                <li className="flex items-start">
                  <Globe className="h-5 w-5 text-primary mr-3 mt-1" />
                  <span className="text-muted">Affected systems and locations</span>
                </li>
                <li className="flex items-start">
                  <MessageSquare className="h-5 w-5 text-primary mr-3 mt-1" />
                  <span className="text-muted">Key contact information</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Need Non-Emergency Assistance?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our security experts for general inquiries and consultations
            </p>
            <button 
              onClick={() => setCurrentPage('contact')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Emergency911;